import { Colors, Heading, MultilineInput } from '@amelio/ui-library';

import {
    CONFIDENTIALITY_NOTICE_ID,
    ConfidentialityNotice,
    type ConfidentialityNoticeProps,
} from './ConfidentialityNotice';

import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReactNode } from 'react';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';
import { ReviewComments } from '@/components/ReviewComments';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import i18n from '@/plugins/i18n';
import { getLocalizedText } from '@/utils/localization';

export type QuestionContentProps = {
    question: ReviewQuestion;
    answerContent: ReactNode;
    confidentiality: ConfidentialityNoticeProps;
    reviewee?: User;
    showComment?: boolean;
    commentValue?: string;
    commentLabel?: string;
    onCommentChange?: (value: string) => void;
    onNext: () => void;
    includeOther: boolean;
    isOtherSelected: boolean;
    otherText: string;
    onOtherTextChange: (value: string) => void;
    showCharLimitWarning: boolean;
    maxOtherTextLength: number;
    disabled: boolean;
    dataTestId?: string;
};

export const QuestionContent = ({
    question,
    answerContent,
    confidentiality,
    reviewee,
    showComment,
    commentValue,
    commentLabel,
    onCommentChange,
    onNext,
    includeOther,
    isOtherSelected,
    otherText,
    onOtherTextChange,
    showCharLimitWarning,
    maxOtherTextLength,
    disabled,
    dataTestId,
}: QuestionContentProps) => {
    const { t } = useAmelioTranslations();
    const isRtl = i18n.language.startsWith('ar');
    const replaceDynamicTags = useReplaceDynamicTags();

    return (
        <div className={'flex w-full max-w-2xl flex-col items-center'}>
            <div className={'mt-8 mb-10 w-full text-center'}>
                <Heading
                    level={2}
                    align={'center'}
                    color={Colors.WHITE}
                    className={'text-xl font-semibold sm:text-2xl'}
                >
                    <QuestionTextRenderer
                        text={replaceDynamicTags({
                            text: getLocalizedText(question.label),
                            employee: reviewee,
                            competencyName: question.competencyName,
                        })}
                    />
                </Heading>
            </div>

            <div className={'w-full text-white'}>{answerContent}</div>

            {includeOther && isOtherSelected && (
                <div className={'mt-8 w-full text-white'}>
                    <MultilineInput
                        value={otherText}
                        onChange={onOtherTextChange}
                        rows={2}
                        className={
                            'focus:border-primary-500 w-full resize-none border-0 border-b bg-transparent px-3 py-2 text-sm focus:outline-none'
                        }
                        placeholder={t('answer.otherOption.placeholder')}
                        dir={isRtl ? 'rtl' : 'auto'}
                        aria-label={t('answer.otherOption.placeholder')}
                        ariaDescribedBy={CONFIDENTIALITY_NOTICE_ID}
                        showTooltip={showCharLimitWarning}
                        autoGrow
                        tooltipText={t('answer.otherOption.charLimitTooltip')}
                        dataTestId={dataTestId ? `${dataTestId}-other-text-input` : undefined}
                        disabled={disabled}
                        maxLength={maxOtherTextLength}
                        textColour={'transparent'}
                        autoFocus
                    />
                </div>
            )}

            {question.isCommentAllowed && (
                <div className={'mt-8 w-full'}>
                    <ReviewComments
                        commentsEnabled={showComment ?? false}
                        value={commentValue}
                        onCommentChange={onCommentChange}
                        onSubmit={onNext}
                        onNextQuestion={onNext}
                        placeholder={commentLabel}
                        label={commentLabel}
                        showTooltip={true}
                        ariaDescribedBy={CONFIDENTIALITY_NOTICE_ID}
                    />
                </div>
            )}

            <ConfidentialityNotice
                {...confidentiality}
                id={CONFIDENTIALITY_NOTICE_ID}
            />
        </div>
    );
};
